import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  Portal,
} from '@chakra-ui/react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const STEPS = [
  {
    target: 'onboarding-username',
    title: 'Welcome to Attributed.to!',
    text: 'Your username is anonymized for your privacy.',
    buttonText: 'Next',
  },
  {
    target: 'onboarding-browse',
    title: 'Browse Tasks',
    text: 'Here you can browse research tasks submitted by others.',
    buttonText: 'Next',
  },
  {
    target: 'onboarding-post',
    title: 'Post a Task',
    text: 'To get started posting your own research tasks, click here!',
    buttonText: 'Got it!',
  },
];

const TOOLTIP_WIDTH = 320;

export const OnboardingTour = () => {
  const { currentUser } = useAuth();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [ready, setReady] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const retryRef = useRef(null);

  const measure = useCallback(() => {
    const el = document.getElementById(STEPS[step].target);
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false;
    const next = { top: r.top, left: r.left, width: r.width, height: r.height };
    setRect((prev) => {
      if (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width && prev.height === next.height) {
        return prev;
      }
      return next;
    });
    return true;
  }, [step]);

  useEffect(() => {
    if (dismissed) return;
    let attempts = 0;
    let stableCount = 0;
    let lastRect = null;

    const tryFind = () => {
      const el = document.getElementById(STEPS[step].target);
      if (!el) {
        attempts++;
        if (attempts < 30) retryRef.current = setTimeout(tryFind, 100);
        return;
      }
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) {
        attempts++;
        if (attempts < 30) retryRef.current = setTimeout(tryFind, 100);
        return;
      }

      const current = `${r.top},${r.left},${r.width},${r.height}`;
      if (current === lastRect) {
        stableCount++;
      } else {
        stableCount = 0;
        lastRect = current;
      }

      // Wait for 2 consecutive stable measurements before showing
      if (stableCount >= 2) {
        measure();
        setTransitioning(false);
        setReady(true);
        return;
      }

      attempts++;
      if (attempts < 30) retryRef.current = setTimeout(tryFind, 100);
    };
    retryRef.current = setTimeout(tryFind, 300);

    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      clearTimeout(retryRef.current);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [step, measure, dismissed]);

  const markComplete = async () => {
    setDismissed(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { completedOnboarding: true });
    } catch (err) {
      console.error('Error saving onboarding state:', err);
    }
  };

  const handleNext = async () => {
    if (step < STEPS.length - 1) {
      setTransitioning(true);
      setStep(step + 1);
    } else {
      await markComplete();
    }
  };

  if (dismissed || !ready || !rect) return null;

  const current = STEPS[step];

  // During transition, keep the overlay with the last known rect but hide the tooltip
  const displayRect = rect;

  // Calculate tooltip position: centered below target, clamped to viewport
  const tooltipTop = displayRect.top + displayRect.height + 14;
  let tooltipLeft = displayRect.left + displayRect.width / 2 - TOOLTIP_WIDTH / 2;
  tooltipLeft = Math.max(12, Math.min(tooltipLeft, window.innerWidth - TOOLTIP_WIDTH - 12));

  // Arrow position: always points to center of target
  const arrowLeft = displayRect.left + displayRect.width / 2 - tooltipLeft;

  return (
    <Portal>
      <style>{`
        @keyframes onboarding-pulse {
          0% { box-shadow: 0 0 0 0 rgba(237, 137, 54, 0.6); }
          70% { box-shadow: 0 0 0 12px rgba(237, 137, 54, 0); }
          100% { box-shadow: 0 0 0 0 rgba(237, 137, 54, 0); }
        }
        @keyframes onboarding-fadein {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Full-screen overlay with cutout around target */}
      <Box position="fixed" inset={0} zIndex={1500}>
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <mask id="ob-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={displayRect.left - 6}
                y={displayRect.top - 6}
                width={displayRect.width + 12}
                height={displayRect.height + 12}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#ob-mask)" />
        </svg>

        {/* Pulse ring */}
        {!transitioning && (
          <div
            style={{
              position: 'fixed',
              top: displayRect.top - 6,
              left: displayRect.left - 6,
              width: displayRect.width + 12,
              height: displayRect.height + 12,
              borderRadius: 8,
              border: '2px solid #ed8936',
              animation: 'onboarding-pulse 1.5s ease-in-out infinite',
              pointerEvents: 'none',
              zIndex: 1501,
            }}
          />
        )}

        {/* Tooltip card */}
        {!transitioning && (
          <div
            style={{
              position: 'fixed',
              top: tooltipTop,
              left: tooltipLeft,
              width: TOOLTIP_WIDTH,
              zIndex: 1502,
              animation: 'onboarding-fadein 0.3s ease-out',
            }}
          >
            {/* Arrow pointing up to target */}
            <div
              style={{
                position: 'absolute',
                top: -6,
                left: arrowLeft,
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid #dd6b20',
                transform: 'translateX(-8px)',
              }}
            />

            <Box
              bg="white"
              borderRadius="xl"
              boxShadow="0 8px 32px rgba(0,0,0,0.25)"
              border="2px solid"
              borderColor="orange.400"
              overflow="hidden"
            >
              <Box bg="orange.500" px={4} py={2}>
                <HStack justify="space-between">
                  <Text color="white" fontSize="xs" fontWeight="semibold">
                    Step {step + 1} of {STEPS.length}
                  </Text>
                  {step < STEPS.length - 1 && (
                    <Text
                      color="whiteAlpha.800"
                      fontSize="xs"
                      cursor="pointer"
                      _hover={{ color: 'white' }}
                      onClick={markComplete}
                    >
                      Skip tour
                    </Text>
                  )}
                </HStack>
              </Box>
              <VStack align="stretch" spacing={3} p={4}>
                <Text fontWeight="bold" fontSize="md" color="gray.800">
                  {current.title}
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">
                  {current.text}
                </Text>
                <Button
                  colorScheme="orange"
                  size="sm"
                  onClick={handleNext}
                  alignSelf="flex-end"
                >
                  {current.buttonText}
                </Button>
              </VStack>
            </Box>
          </div>
        )}
      </Box>
    </Portal>
  );
};
